/**
 * The named-source selector and the identity readout, always visible in the
 * tab header (ClickUp z8nrz7c366 requirement 3: during a live test window,
 * "no messages" must never be ambiguous about WHICH source is being read).
 *
 * The identity is rendered VERBATIM from the source's own `deployment` block
 * (requirement 4) — the UI never assumes. Three states, and the middle one is
 * the whole point:
 *
 * 1. confirmed — the source answered `/status`; show role/environment/host and
 *    a badge from `receivesPartnerTraffic`.
 * 2. waiting — a source is selected but has not answered yet (first poll, or
 *    just after a switch cleared the cache). Showing the PREVIOUS source's
 *    identity next to the new source's name is exactly the ambiguity the
 *    ticket exists to remove, so this state says so instead.
 * 3. unreachable — the error banner below carries the detail; the identity
 *    area claims nothing.
 *
 * Switching is dropdown-only, from the server-side allowlist. There is no URL
 * or token field by design: the backend does the fetching now, so a free-text
 * host would be server-side request forgery, not a convenience.
 */
import { Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Hl7Deployment, Hl7SourceSummary } from '@/lib/dataLayer/types'

export type SourceChromeState = 'confirmed' | 'waiting' | 'unreachable'

export function DataLayerSourceChrome({
  sources,
  sourceId,
  deployment,
  state,
  canManage,
  onSelect,
}: {
  /** Undefined while the allowlist itself is still loading. */
  sources: Hl7SourceSummary[] | undefined
  sourceId: string | null
  /** The source's own identity block; null until confirmed. */
  deployment: Hl7Deployment | null
  state: SourceChromeState
  canManage: boolean
  onSelect: (id: string) => void
}) {
  const label = sources?.find((source) => source.id === sourceId)?.label ?? sourceId ?? '…'

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canManage && sources !== undefined && sourceId !== null ? (
        <Select value={sourceId} onValueChange={onSelect}>
          <SelectTrigger
            className="h-8 w-auto gap-1.5 font-mono text-xs"
            aria-label="Data layer source"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {sources.map((source) => (
              <SelectItem key={source.id} value={source.id} className="text-xs">
                {source.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="font-mono text-[11px] text-muted-foreground">{label}</span>
      )}

      {state === 'confirmed' && deployment !== null && (
        <>
          {deployment.receivesPartnerTraffic ? (
            <Badge variant="destructive" className="text-[10px] uppercase tracking-wide">
              Real partner traffic
            </Badge>
          ) : (
            <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">
              Synthetic
            </Badge>
          )}
          <span className="font-mono text-[11px] text-muted-foreground" title="What this source reports about itself">
            {deployment.role} · {deployment.environment} · {deployment.host}
          </span>
        </>
      )}

      {state === 'waiting' && (
        <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          waiting for this source to identify itself
        </span>
      )}
    </div>
  )
}
